<script lang="ts">
    import type {Loan} from 'typings/portaro.be.types';
    import {getDateFormatter, getLocalization} from 'core/svelte-context/context';
    import {pipe} from 'core/utils';
    import {loc, strParams} from 'shared/utils/pipes';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpLoansGenericTable from './KpLoansGenericTable.svelte';
    import LoanDocument from './LoanDocument.svelte';
    import KpPopover from 'shared/ui-widgets/popover/KpPopover.svelte';
    import {fade} from 'svelte/transition';
    import type {PageableList, PageableListState} from 'shared/utils/reactive-pageable-list';
    import type {EmptyObject} from 'typings/portaro.fe.types';
    import type {Observable} from 'rxjs';
    import {isNullOrUndefined} from 'shared/utils/custom-utils';

    export let loanList: PageableList<Loan, string, EmptyObject>;
    export let canLoanReturnAny: boolean;
    export let returnLoan: (loan: Loan) => void;
    export let renewLoan: (loan: Loan) => void;

    const localize = getLocalization();
    const dateFormatter = getDateFormatter();

    let loanListData: Observable<PageableListState<Loan, string, EmptyObject>>;
    $: loanListData = loanList.getState$();

    function showRenewalColumn(items: Loan[]) {
        return (items ?? []).some((loan) => loan.renewability.enabled);
    }
</script>

<KpLoansGenericTable {loanList}>
    <tr slot="header">
        <th class="header-document-name">{localize(/* @kp-localization commons.nazevDokumentu */ 'commons.nazevDokumentu')}</th>
        <th class="header-due-date">{localize(/* @kp-localization loan.TerminVraceni */ 'loan.TerminVraceni')}</th>
        <th class="header-last-renewal">{localize(/* @kp-localization konto.posledniProlongace */ 'konto.posledniProlongace')}</th>
        <th class="header-overdue-notices-count">{localize(/* @kp-localization konto.pocetUpominek */ 'konto.pocetUpominek')}</th>
        <th class="header-location">{localize(/* @kp-localization exemplar.location */ 'exemplar.location')}</th>
        <th class="header-department">{localize(/* @kp-localization ctenar.rental */ 'ctenar.rental')}</th>
        {#if showRenewalColumn($loanListData.loadedItems)}
            <th class="header-renew">{localize(/* @kp-localization loan.Prodlouzit */ 'loan.Prodlouzit')}</th>
        {/if}
        {#if canLoanReturnAny}
            <th class="header-return">{localize(/* @kp-localization loan.Vratit */ 'loan.Vratit')}</th>
        {/if}
        <th class="header-more-info">{localize(/* @kp-localization commons.ViceInformaci */ 'commons.ViceInformaci')}</th>
    </tr>

    <svelte:fragment slot="body">
        {#each $loanListData.loadedItems ?? [] as loan(loan.id)}
            <tr class="loan-item"
                in:fade
                class:loan-ending-soon={loan.endingLoan}
                class:loan-expired={loan.expiredLoan}>

                <td class="data-document-name">
                    <LoanDocument loan="{loan}" showLoanCount="{true}"/>
                </td>

                <td class="data-due-date centered-loan-data"
                    class:error-warning={loan.endingLoan || loan.expiredLoan}>
                    {#if isNullOrUndefined(loan.dueDate)}
                        -
                    {:else if loan.endingLoan || loan.expiredLoan}
                        <IconedContent icon="exclamation" iconColor="var(--danger-red)">
                            {pipe(loan.dueDate, dateFormatter('d.M.yyyy'))}
                        </IconedContent>
                    {:else}
                        {pipe(loan.dueDate, dateFormatter('d.M.yyyy'))}
                    {/if}
                </td>

                <td class="data-last-renewal centered-loan-data">
                    {#if loan.lastRenewalDate}
                        {pipe(loan.lastRenewalDate, dateFormatter('d.M.yyyy'))}
                    {/if}
                </td>

                <td class="data-overdue-notices-count centered-loan-data"
                    class:error-warning={loan.overdueNoticesCount > 0}>
                    {loan.overdueNoticesCount}
                </td>

                <td class="data-location centered-loan-data">
                    {#if loan.location}
                        {pipe(loan.location, loc())}
                    {:else}
                        -
                    {/if}
                </td>

                <td class="data-department centered-loan-data">{pipe(loan.department, loc())}</td>

                {#if showRenewalColumn($loanListData.loadedItems)}
                    <td class="data-renew centered-loan-data" class:error-warning={!loan.renewability.renewable}>
                        {#if loan.renewability.renewable}
                            <KpButton buttonSize="sm" on:click={() => renewLoan(loan)}>
                                <IconedContent icon="time-forward">
                                    {pipe(localize(/* @kp-localization loan.ProdlouzitOXDni */ 'loan.ProdlouzitOXDni'), strParams(loan.renewability.numberOfDays.toString()))}
                                </IconedContent>
                            </KpButton>
                        {:else}
                            <IconedContent icon="exclamation" iconColor="var(--danger-red)" orientation="vertical">
                                {loan.renewability.text}
                            </IconedContent>
                        {/if}
                    </td>
                {/if}

                {#if canLoanReturnAny}
                    <td class="data-return centered-loan-data">
                        <KpButton buttonSize="sm" on:click={() => returnLoan(loan)}>
                            <IconedContent icon="redo-alt">
                                {localize(/* @kp-localization loan.Vratit */ 'loan.Vratit')}
                            </IconedContent>
                        </KpButton>
                    </td>
                {/if}

                <td class="centered-loan-data">
                    <KpPopover buttonSize="sm" additionalPopoverPanelClasses="active-loans-popover-panel">
                        <IconedContent slot="button" icon="comment-info">Info</IconedContent>
                        <span slot="popover-title">{localize(/* @kp-localization commons.ViceInformaci */ 'commons.ViceInformaci')}</span>
                        <div slot="popover-content">
                            <ul class="more-info-list">
                                <li class="info-item">
                                    <span>{localize(/* @kp-localization loan.DatumVypujceni */ 'loan.DatumVypujceni')}</span>
                                    <span class="info-value">
                                        {#if loan.lendDate}
                                            {pipe(loan.lendDate, dateFormatter('d.M.yyyy'))}
                                        {:else}
                                            -
                                        {/if}
                                    </span>
                                </li>

                                <li class="info-item">
                                    <span>{localize(/* @kp-localization loan.TerminVraceni */ 'loan.TerminVraceni')}</span>
                                    <span class="info-value">
                                        {#if loan.dueDate}
                                            {pipe(loan.dueDate, dateFormatter('d.M.yyyy'))}
                                        {:else}
                                            -
                                        {/if}
                                    </span>
                                </li>

                                <li class="info-item">
                                    <span>{localize(/* @kp-localization konto.posledniProlongace */ 'konto.posledniProlongace')}</span>
                                    <span class="info-value">
                                        {#if loan.lastRenewalDate}
                                            {pipe(loan.lastRenewalDate, dateFormatter('d.M.yyyy'))}
                                        {:else}
                                            -
                                        {/if}
                                    </span>
                                </li>

                                <li class="info-item">
                                    <span>{localize(/* @kp-localization konto.pocetUpominek */ 'konto.pocetUpominek')}</span>
                                    <span class="info-value">{loan.overdueNoticesCount}</span>
                                </li>

                                <li class="info-item">
                                    <span>{localize(/* @kp-localization exemplar.loanCategory */ 'exemplar.loanCategory')}</span>
                                    <span class="info-value">
                                        {#if loan.loanCategory}
                                            {pipe(loan.loanCategory, loc())}
                                        {:else}
                                            -
                                        {/if}
                                    </span>
                                </li>

                                <li class="info-item">
                                    <span>{localize(/* @kp-localization exemplar.location */ 'exemplar.location')}</span>
                                    <span class="info-value">
                                        {#if loan.location}
                                            {pipe(loan.location, loc())}
                                        {:else}
                                            -
                                        {/if}
                                    </span>
                                </li>

                                <li class="info-item">
                                    <span>{localize(/* @kp-localization ctenar.rental */ 'ctenar.rental')}</span> <span
                                        class="info-value">
                                        {#if loan.department}
                                            {pipe(loan.department, loc())}
                                        {:else}
                                            -
                                        {/if}
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </KpPopover>
                </td>
            </tr>
        {/each}
    </svelte:fragment>
</KpLoansGenericTable>

<style lang="less">
    @import (reference) "bootstrap-less/bootstrap/variables";

    @ending-soon-bg: #FFEEE4;
    @expired-bg: #FFE9EC;

    .loan-item {
        &.loan-ending-soon {
            background-color: @ending-soon-bg;
        }

        &.loan-expired {
            background-color: @expired-bg;
        }
    }

    .more-info-list {
        display: flex;
        flex-direction: column;

        .info-item {
            display: flex;
            gap: 30px;
            align-items: center;
            justify-content: space-between;
            border-bottom: 1px solid @table-border-color;
            padding: @padding-large-vertical @padding-large-horizontal;

            &:last-child {
                border-bottom: none;
            }

            .info-value {
                font-weight: 500;
            }
        }
    }

    :global {
        .active-loans-popover-panel {
            --popover-content-padding: 0;
        }
    }
</style>
